import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '../context/AppContext';
import { useT } from '../i18n/translations';
import { PRODUCTS, CATEGORIES } from '../data/catalog';
import Icon from './Icon';
import './SearchOverlay.css';

const TRENDING = ['原神', 'GASH', 'PSN', 'Steam', 'Netflix', 'Razer Gold', 'VALORANT', '星穹鐵道', 'Apple 禮物卡', 'Switch'];

export default function SearchOverlay() {
  const { lang, drawer, closeDrawer, navigate, fx } = useApp();
  const t = useT(lang);
  const [q, setQ] = useState('');
  const [focusIdx, setFocusIdx] = useState(0);
  const inputRef = useRef(null);

  const open = drawer.open === 'search';

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQ('');
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        open ? closeDrawer() : (window.dispatchEvent(new CustomEvent('nx-open-search')));
      }
      if (e.key === 'Escape' && open) closeDrawer();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, closeDrawer]);

  const results = useMemo(() => {
    if (!q.trim()) return PRODUCTS.filter((p) => p.hot).slice(0, 6);
    const Q = q.toLowerCase();
    return PRODUCTS.filter(
      (p) =>
        p.name.toLowerCase().includes(Q) ||
        p.en.toLowerCase().includes(Q) ||
        p.publisher.toLowerCase().includes(Q) ||
        p.tagline.toLowerCase().includes(Q)
    ).slice(0, 8);
  }, [q]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusIdx((i) => Math.min(results.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      const hit = results[focusIdx];
      if (hit) {
        closeDrawer();
        navigate('product', { id: hit.id });
      }
    }
  };

  if (!open) return null;

  return (
    <div className="nx-search" role="dialog" aria-modal="true">
      <div className="nx-search__backdrop" onClick={closeDrawer} />
      <div className="nx-search__panel">
        <div className="nx-search__inputrow">
          <Icon name="search" size={18} />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setFocusIdx(0);
            }}
            onKeyDown={onKeyDown}
            placeholder={t.nav.search}
            aria-label="Search"
          />
          {q && (
            <button className="nx-search__clear" onClick={() => setQ('')}>
              <Icon name="close" size={14} />
            </button>
          )}
          <kbd>ESC</kbd>
        </div>

        {!q.trim() && (
          <div className="nx-search__section">
            <div className="nx-search__shead">
              <Icon name="fire" size={12} /> 熱門搜尋
            </div>
            <div className="nx-search__trend">
              {TRENDING.map((t, i) => (
                <button
                  key={t}
                  className="nx-search__trend-chip"
                  onClick={() => setQ(t)}
                >
                  <span>{i + 1}</span> {t}
                </button>
              ))}
            </div>
            <div className="nx-search__shead">
              <Icon name="sparkle" size={12} /> 快速分類
            </div>
            <div className="nx-search__cats">
              {CATEGORIES.filter((c) => c.id !== 'all').map((c) => (
                <button
                  key={c.id}
                  className="nx-search__cat"
                  onClick={() => {
                    closeDrawer();
                    navigate('category', { id: c.id });
                  }}
                >
                  <Icon name={c.icon} size={16} />
                  {c.name[lang] || c.name['zh-TW']}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="nx-search__section">
          <div className="nx-search__shead">
            <Icon name="check" size={12} />{' '}
            {q ? `${results.length} 個結果` : '為你推薦'}
          </div>
          <ul className="nx-search__results">
            {results.length === 0 ? (
              <li className="nx-search__empty">
                找不到「{q}」的相關商品。
              </li>
            ) : (
              results.map((p, i) => (
                <li
                  key={p.id}
                  className={`nx-search__result ${i === focusIdx ? 'is-focus' : ''}`}
                  onMouseEnter={() => setFocusIdx(i)}
                  onClick={() => {
                    closeDrawer();
                    navigate('product', { id: p.id });
                  }}
                >
                  <span
                    className="nx-search__result-icon brand-tile"
                    style={{ '--brand-bg': p.bg }}
                  >
                    {p.logoUrl ? (
                      <img src={p.logoUrl} alt={p.en} loading="lazy" />
                    ) : (
                      <em>{p.logoText || p.en.slice(0, 2)}</em>
                    )}
                  </span>
                  <div className="nx-search__result-info">
                    <div className="nx-search__result-name">{p.name}</div>
                    <div className="nx-search__result-meta">
                      {p.publisher} · {p.tagline}
                    </div>
                  </div>
                  <div className="nx-search__result-price">
                    {fx(p.denominations[0].price)}+
                  </div>
                  <Icon name="arrowRight" size={14} />
                </li>
              ))
            )}
          </ul>
        </div>

        <div className="nx-search__foot">
          <span><kbd>↑</kbd><kbd>↓</kbd> 切換</span>
          <span><kbd>⏎</kbd> 開啟</span>
          <span><kbd>esc</kbd> 關閉</span>
        </div>
      </div>
    </div>
  );
}
